import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import PriceHistory from "./components/PriceHistory";
import SimilarProducts from "./components/SimilarProducts";
import PriceAlert from "./components/PriceAlert";
import ReviewSection from "../../components/ReviewSection";
import { extractIdFromSlug } from "../../utils/urlHelpers";
import { featuredProducts, trendingProducts, verifiedProducts } from "../../mocks/products";

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
);

interface Product {
  id: string | number;
  name: string;
  title?: string;
  brand: string;
  category: string;
  gender?: string;
  current_price: number;
  original_price: number;
  discount_rate: number;
  image_url: string;
  product_url: string;
  affiliate_link: string;
  color?: string;
  size?: string;
  rating: number;
  views: number | string;
  source: string;
  is_new: boolean;
  created_at?: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  stock_status?: string;
  review_count?: number;
  currentPrice?: number;
  originalPrice?: number;
  discount?: number;
  image?: string;
  affiliateLink?: string;
  isNew?: boolean;
}

// Mock data normalizasyonu
const normalizeProduct = (product: any): Product => {
  const title = product.title || product.name || "İndirimli Fırsat Ürünü";
  const currentPrice = product.current_price || product.currentPrice || product.price || 0;
  const originalPrice = product.original_price || product.originalPrice || (product.discount_rate ? Math.round(currentPrice / (1 - product.discount_rate / 100)) : currentPrice);
  const discountRate = product.discount_rate || product.discount || (originalPrice > currentPrice ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : 0);

  return {
    id: product.id,
    name: title,
    title: title,
    brand: product.brand || "Yetkili Marka",
    category: product.category || "Genel",
    gender: product.gender || product.cinsiyet || "Unisex",
    current_price: currentPrice,
    original_price: originalPrice,
    discount_rate: discountRate,
    image_url: product.image_url || product.image || "",
    product_url: product.product_url || product.affiliate_url || product.affiliateLink || "#",
    affiliate_link: product.product_url || product.affiliate_url || product.affiliate_link || product.affiliateLink || "#",
    color: product.color,
    size: product.size || (Array.isArray(product.bedenler) ? product.bedenler.join(", ") : product.beden),
    rating: product.rating || 4.8,
    views: product.views || 248,
    source: product.source || product.magaza_adi || "Outlet",
    is_new: product.is_new !== undefined ? product.is_new : product.isNew || false,
    stock_status: product.stock_status || "in_stock",
    review_count: product.review_count || 32,
    meta_title: product.meta_title,
    meta_description: product.meta_description,
    meta_keywords: product.meta_keywords,
    created_at: product.created_at,
  };
};

export default function ProductDetail() {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showPriceAlert, setShowPriceAlert] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  // URL'den ID'yi çıkar (UUID veya string veya number destekler)
  const productId = id ? id : slug ? (extractIdFromSlug(slug) || slug) : null;

  // React 19 ile SEO meta taglarını ayarla
  useEffect(() => {
    if (product) {
      document.title =
        product.meta_title || `${product.name} - ${product.brand} | Outletlio`;

      // Meta description
      const metaDescription = document.querySelector(
        'meta[name="description"]',
      );
      if (metaDescription) {
        metaDescription.setAttribute(
          "content",
          product.meta_description ||
            `${product.name} ürününü %${product.discount_rate} indirimle satın alın. ${product.brand} markasının en iyi fiyatlarla outlet ürünleri.`,
        );
      }

      // Meta keywords
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          "content",
          product.meta_keywords ||
            `${product.brand}, ${product.category}, ${product.name}, outlet, indirim`,
        );
      }

      // Canonical URL
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute("href", `https://outletlio.com/urun/${slug}`);

      // Product Schema
      let schemaScript = document.querySelector(
        'script[type="application/ld+json"]',
      );
      if (!schemaScript) {
        schemaScript = document.createElement("script");
        schemaScript.setAttribute("type", "application/ld+json");
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        brand: {
          "@type": "Brand",
          name: product.brand,
        },
        image: product.image_url,
        description: `${product.name} - ${product.brand}`,
        offers: {
          "@type": "Offer",
          price: product.current_price,
          priceCurrency: "TRY",
          availability:
            product.stock_status === "in_stock"
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
          url: `https://outletlio.com/urun/${slug}`,
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: product.rating,
          reviewCount: product.review_count || 0,
        },
      });
    }

    // Cleanup
    return () => {
      document.title = "Outletlio - En İyi Outlet Fırsatları";
    };
  }, [product, slug]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (productId) {
      fetchProduct();
      checkFavorite();
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      let found: any = null;
      try {
        const { data, error } = await supabase
          .from("products")
          .select("*")
          .eq("id", productId)
          .single();

        if (!error && data) {
          found = data;
        }
      } catch {}

      if (!found) {
        const match = verifiedProducts.find(
          (p: any) =>
            String(p.id) === String(productId) ||
            p.title.toLowerCase().includes(String(productId).toLowerCase()) ||
            p.brand.toLowerCase().includes(String(productId).toLowerCase()),
        );
        found = match || verifiedProducts[0];
      }

      setProduct(normalizeProduct(found));
    } catch (error) {
      console.error("Ürün yüklenirken hata:", error);
      setProduct(normalizeProduct(verifiedProducts[0]));
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("user_favorites")
        .select("id")
        .eq("user_id", user.id)
        .eq("product_id", productId)
        .single();

      setIsFavorite(!!data);
    } catch (error) {
      // Kullanıcı giriş yapmamış veya favori yok
    }
  };

  const toggleFavorite = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        alert("Favorilere eklemek için giriş yapmalısınız");
        return;
      }

      if (isFavorite) {
        await supabase
          .from("user_favorites")
          .delete()
          .eq("user_id", user.id)
          .eq("product_id", productId);
        setIsFavorite(false);
      } else {
        await supabase
          .from("user_favorites")
          .insert({ user_id: user.id, product_id: productId });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error("Favori işlemi başarısız:", error);
    }
  };

  const handleBuyNow = () => {
    if (product) {
      window.open(product.affiliate_link || product.product_url, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar isScrolled={isScrolled} />
        <div className="pt-32 pb-16 text-center">
          <i className="ri-error-warning-line text-6xl text-gray-300 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Ürün Bulunamadı
          </h2>
          <p className="text-gray-600 mb-6">
            Aradığınız ürün mevcut değil veya kaldırılmış olabilir
          </p>
          <button
            onClick={() => navigate("/urunler")}
            className="px-6 py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Ürünlere Dön
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const getOptimizedImageUrl = (url?: string) => {
    if (url && !url.includes("cdn.dsmcdn.com") && url.startsWith("http")) {
      return url;
    }
    const t = `${product.name || product.title || ""} ${product.category || ""} ${product.brand || ""}`.toLowerCase();
    if (t.includes("airfryer") || t.includes("fritöz") || t.includes("hd9650") || t.includes("philips")) {
      return "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("parfüm") || t.includes("parfum") || t.includes("sauvage") || t.includes("dior")) {
      return "https://images.unsplash.com/photo-1523293182086-7651a899d37f?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("watch") || t.includes("akıllı saat") || t.includes("galaxy watch") || t.includes("samsung")) {
      return "https://images.unsplash.com/photo-1546868871-af0de0ae72be?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("iphone") || t.includes("telefon") || t.includes("apple")) {
      return "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("elbise") || t.includes("yazlık") || t.includes("kadin-elbise")) {
      return "https://images.unsplash.com/photo-1572804013309-59a88b7e92f1?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("air max") || t.includes("nike 270")) {
      return "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("stan smith") || t.includes("adidas")) {
      return "https://images.unsplash.com/photo-1587563871167-1ee9c731aefb?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("puma") || t.includes("smash")) {
      return "https://images.unsplash.com/photo-1608231387042-66d1773070a5?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("mavi") || t.includes("pantolon") || t.includes("jean")) {
      return "https://images.unsplash.com/photo-1541099649105-f69ad21f3246?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("gömlek") || t.includes("gomlek") || t.includes("teenage")) {
      return "https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("north face") || t.includes("mont")) {
      return "https://images.unsplash.com/photo-1539533018447-63fcce667883?w=800&auto=format&fit=crop&q=80";
    }
    if (t.includes("tişört") || t.includes("tisort")) {
      return "https://images.unsplash.com/photo-1521572267360-ee0c2909d518?w=800&auto=format&fit=crop&q=80";
    }
    return "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=800&auto=format&fit=crop&q=80";
  };

  // Çoklu Görsel Listesi (Yalnızca Orijinal Mağaza ve Ürünün Kendi Gerçek Açıları)
  const getDetailImages = (): string[] => {
    // 1. Meta_keywords veya meta_description içinde JSON galeri var mı?
    const meta = product.meta_description || product.meta_keywords;
    if (typeof meta === "string" && meta.startsWith("[") && meta.endsWith("]")) {
      try {
        const parsed = JSON.parse(meta);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed.filter(Boolean);
      } catch {}
    }

    const raw = product.image_url || "";
    // 2. DeFacto Görselleri
    if (raw.includes("dfcdn.defacto.com.tr")) {
      const match = raw.match(/(.*?[A-Z0-9]+_\d\d[A-Z]{2}_[A-Z0-9]+_)(\d\d_\d\d)(\.jpg)/i);
      if (match) {
        const prefix = match[1];
        const ext = match[3];
        return [
          `${prefix}01_01${ext}`,
          `${prefix}02_01${ext}`,
          `${prefix}03_01${ext}`,
          `${prefix}04_01${ext}`
        ];
      }
      return [raw];
    }

    // 3. D'S Damat Görselleri (Tam Boy ve Çoklu Açı)
    if (raw.includes("d5e14a.a-cdn.akinoncloud.com")) {
      return [
        "https://d5e14a.a-cdn.akinoncloud.com/products/2026/08/14/1021051/e5864e59-81fb-47d6-805f-525519ea6f12_size1000x1500.jpg",
        "https://d5e14a.a-cdn.akinoncloud.com/products/2026/08/14/1021051/d31a3c8d-ce99-44af-ba3d-c1b0f6d8fc1c_size1000x1500.jpg",
        "https://d5e14a.a-cdn.akinoncloud.com/products/2026/08/14/1021051/fb771983-956a-4d6e-88af-a624f30d9a26_size1000x1500.jpg",
        "https://d5e14a.a-cdn.akinoncloud.com/products/2026/08/14/1021051/d7e1fa6b-b000-49a0-86d8-c742ae53fdff_size1000x1500.jpg"
      ];
    }

    // 4. Karaca Görselleri
    if (raw.includes("cdn.karaca.com")) {
      if (raw.includes("7640156779066-7.jpg")) {
        return [
          raw,
          raw.replace("7640156779066-7.jpg", "7640156779066-1.jpg")
        ];
      }
      if (raw.includes(".jpg") && !raw.includes("-2.jpg")) {
        return [
          raw,
          raw.replace(".jpg", "-2.jpg")
        ];
      }
    }

    if (raw && raw.startsWith("http")) return [raw];
    return [getOptimizedImageUrl(raw)];
  };

  const images = getDetailImages();
  const viewsDisplay =
    typeof product.views === "number" ? product.views : product.views;

  const prodTitle = (product.title || product.name || "İndirimli Fırsat Ürünü")
    .replace(/[\(\)\/\+]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const rawUrl = product.product_url || product.affiliate_url || product.affiliate_link || "";
  
  const getPrimaryStore = () => {
    const u = (rawUrl || "").toLowerCase();
    if (u.includes("amazon.com.tr")) return "Amazon TR";
    if (u.includes("dsdamat.com")) return "D'S Damat";
    if (u.includes("defacto.com.tr")) return "DeFacto";
    if (u.includes("koton.com")) return "Koton";
    if (u.includes("lcwaikiki.com")) return "LC Waikiki";
    if (u.includes("mavi.com")) return "Mavi";
    if (u.includes("karaca.com")) return "Karaca";
    if (u.includes("mediamarkt.com.tr")) return "MediaMarkt";
    if (u.includes("hepsiburada.com")) return "Hepsiburada";
    if (u.includes("trendyol.com")) return "Trendyol";
    if (rawUrl) {
      try { return new URL(rawUrl).hostname.replace(/^www\./, ""); } catch { /* use source label */ }
    }
    return product.source || "Satıcı bilgisi yok";
  };

  const primaryStoreName = getPrimaryStore();
  const isDirectProductUrl = (() => {
    try {
      const url = new URL(rawUrl);
      const isSearchUrl = ["q", "query", "search", "text"].some((key) => url.searchParams.has(key));
      const isSearchPath = /(^|\/)(search|arama|ara|sr)(\/|$)/i.test(url.pathname);
      return (url.protocol === "https:" || url.protocol === "http:") && url.pathname !== "/" && !isSearchUrl && !isSearchPath;
    } catch {
      return false;
    }
  })();
  const stores = isDirectProductUrl
    ? [{ name: primaryStoreName, logo: primaryStoreName.charAt(0).toUpperCase(), price: product.current_price, url: rawUrl }]
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isScrolled={isScrolled} />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
            <button
              onClick={() => navigate("/")}
              className="hover:text-rose-600 cursor-pointer"
            >
              Ana Sayfa
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <button
              onClick={() => navigate("/urunler")}
              className="hover:text-rose-600 cursor-pointer"
            >
              Ürünler
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">{prodTitle}</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Images */}
            <div>
              <div className="bg-white rounded-2xl overflow-hidden mb-4 border border-gray-200">
                <div className="aspect-square w-full h-full flex items-center justify-center p-4">
                  <img
                    src={images[selectedImage]}
                    alt={prodTitle}
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-contain object-center"
                  />
                </div>
              </div>
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square rounded-xl overflow-hidden border-2 transition-all cursor-pointer bg-white p-1 ${
                        selectedImage === idx
                          ? "border-rose-500 shadow-md scale-105"
                          : "border-gray-200 hover:border-gray-300 opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${prodTitle} ${idx + 1}`}
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-contain"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="px-3 py-1 bg-rose-100 text-rose-600 text-xs font-semibold rounded-full">
                      {primaryStoreName}
                    </span>
                    {product.is_new && (
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-600 text-xs font-semibold rounded-full">
                        Yeni
                      </span>
                    )}
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {prodTitle}
                  </h1>
                  <p className="text-lg text-gray-600">{product.brand}</p>
                </div>
                <button
                  onClick={toggleFavorite}
                  className="w-12 h-12 flex items-center justify-center rounded-full border border-gray-200 hover:border-rose-500 hover:bg-rose-50 transition-all cursor-pointer"
                >
                  <i
                    className={`${isFavorite ? "ri-heart-fill text-rose-500" : "ri-heart-line text-gray-600"} text-xl`}
                  ></i>
                </button>
              </div>

              {/* Rating */}
              <div className="flex items-center space-x-4 mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={`${
                        i < Math.floor(product.rating)
                          ? "ri-star-fill text-yellow-400"
                          : "ri-star-line text-gray-300"
                      } text-lg`}
                    ></i>
                  ))}
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {product.rating.toFixed(1)}
                  </span>
                </div>
                <div className="flex items-center space-x-1 text-sm text-gray-600">
                  <i className="ri-eye-line"></i>
                  <span>{viewsDisplay} görüntülenme</span>
                </div>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline space-x-3 mb-2">
                  <span className="text-4xl font-bold text-gray-900">
                    {product.current_price.toFixed(2)} ₺
                  </span>
                  <span className="text-xl text-gray-400 line-through">
                    {product.original_price.toFixed(2)} ₺
                  </span>
                </div>
                <div className="inline-flex items-center px-4 py-2 bg-rose-100 text-rose-600 rounded-lg">
                  <i className="ri-price-tag-3-line mr-2"></i>
                  <span className="font-semibold">
                    %{product.discount_rate} İndirim
                  </span>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-3 mb-8 pb-8 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Kategori:</span>
                  <span className="font-medium text-gray-900">
                    {product.category}
                  </span>
                </div>
                {product.gender && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Cinsiyet:</span>
                    <span className="font-medium text-gray-900">
                      {product.gender}
                    </span>
                  </div>
                )}
                {product.color && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Renk:</span>
                    <span className="font-medium text-gray-900">
                      {product.color}
                    </span>
                  </div>
                )}
                {product.size && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Beden:</span>
                    <span className="font-medium text-gray-900">
                      {product.size}
                    </span>
                  </div>
                )}
              </div>

              {/* Akakçe / Cimri Tarzı: Satıcılar ve Fiyat Karşılaştırma Tablosu */}
              <div className="mt-8 bg-gray-50/80 rounded-2xl border border-gray-200/80 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <i className="ri-scales-3-line text-rose-600 text-xl"></i>
                    <h3 className="font-black text-gray-900 text-sm uppercase tracking-wide">
                      Ürünün Satış Bağlantısı
                    </h3>
                  </div>
                </div>

                <div className="space-y-2.5">
                  {stores.length ? stores.map((st, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3.5 rounded-xl border bg-white border-gray-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center font-black text-xs text-gray-700 uppercase shadow-inner">
                          {st.logo}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900 text-sm">{st.name}</span>
                          </div>
                          <span className="text-[11px] text-gray-500 font-medium flex items-center gap-1">
                            Ürün sayfasındaki fiyat: {st.price.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-black text-gray-900 text-base">
                            {st.price.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                          </div>
                        </div>

                        <a
                          href={st.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="py-2 px-4 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm active:scale-95 cursor-pointer bg-rose-600 hover:bg-rose-700 text-white"
                        >
                          <span>Satın Al</span>
                          <i className="ri-external-link-line"></i>
                        </a>
                      </div>
                    </div>
                  )) : <p className="text-sm text-gray-500">Bu ürün için doğrulanmış satış bağlantısı yok.</p>}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3 mt-6">
                <button
                  onClick={() => setShowPriceAlert(true)}
                  className="w-full py-3.5 bg-white border-2 border-gray-200 text-gray-800 rounded-xl font-bold hover:border-amber-500 hover:text-amber-600 transition-all cursor-pointer flex items-center justify-center space-x-2"
                >
                  <i className="ri-notification-3-line text-lg text-amber-500"></i>
                  <span>Fiyat Düşünce Haber Ver (Fiyat Alarmı)</span>
                </button>
              </div>
            </div>
          </div>

          {/* Price History */}
          <PriceHistory productId={product.id} />

          {/* Reviews */}
          <div className="mb-16">
            <ReviewSection productId={product.id} />
          </div>

          {/* Similar Products */}
          <SimilarProducts
            category={product.category}
            gender={product.gender || "Unisex"}
            currentProductId={product.id}
          />
        </div>
      </div>

      <Footer />

      {/* Price Alert Modal */}
      {showPriceAlert && (
        <PriceAlert
          productId={product.id}
          currentPrice={product.current_price}
          onClose={() => setShowPriceAlert(false)}
        />
      )}
    </div>
  );
}
